/**
 * MedsManagerCard — „Лекарства, добавки и билки" в профила.
 * Пълно управление: добавяне, редакция на доза/честота/часове, изтриване.
 * Общият списък захранва чинията, гърдата, черния дроб, лабораториите и ум и тяло.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Pill, Leaf, FlaskConical, Plus, Trash2, Clock, Loader2, ArrowRight, Upload, Pencil, Check, X, Copy,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const KINDS = [
  { key: "drug", label: "Лекарство", Icon: Pill, color: "#8B5CF6" },
  { key: "supplement", label: "Добавка", Icon: FlaskConical, color: "#0EA5E9" },
  { key: "herbal", label: "Билка", Icon: Leaf, color: "#10B981" },
];
const EMPTY = { name: "", kind: "drug", dose: "", frequency: "", schedule: "", pack: "" };

export default function MedsManagerCard() {
  const { API, user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(EMPTY);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [suggest, setSuggest] = useState([]);
  const [picked, setPicked] = useState(null);
  const [autoNote, setAutoNote] = useState(null);
  const [manual, setManual] = useState(false);
  const [herb, setHerb] = useState(null);       // предупреждения за избрания запис
  const [herbActive, setHerbActive] = useState([]);  // активните двойки в списъка

  const loadHerbActive = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/medications/herb-check`);
      setHerbActive(data?.active || []);
    } catch { setHerbActive([]); }
  }, [API]);

  useEffect(() => { loadHerbActive(); }, [loadHerbActive]);

  const freetext = String(user?.profile?.health?.medications || "").trim();

  // търсене в българския лекарствен указател (ИАЛ + централно разрешени)
  useEffect(() => {
    const q = form.name.trim();
    if (q.length < 2 || picked === q) { setSuggest([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/medications/registry/search`,
          { params: { q, kind: form.kind } });
        setSuggest(data?.items || []);
      } catch { setSuggest([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [form.name, form.kind, picked, API]);

  // при ръчно въведено име също проверяваме за взаимодействия
  useEffect(() => {
    const q = form.name.trim();
    if (!manual || q.length < 3) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/medications/herb-check`,
          { params: { name: q, kind: form.kind } });
        setHerb({ name: q, warnings: data?.candidate || [], catalog: data?.catalog || null });
      } catch { /* noop */ }
    }, 500);
    return () => clearTimeout(t);
  }, [form.name, form.kind, manual, API]);

  // избор от указателя → автоматична доза и часове
  const pickFromRegistry = async (item) => {
    setSuggest([]);
    setPicked(item.name);
    setForm((f) => ({ ...f, name: item.name }));
    // веднага проверяваме за взаимодействия с вече въведените лекарства
    try {
      const { data: h } = await axios.get(`${API}/medications/herb-check`, {
        params: { name: item.name, kind: form.kind },
      });
      setHerb({ name: item.name, warnings: h?.candidate || [], catalog: h?.catalog || null });
      setHerbActive(h?.active || []);
    } catch { setHerb(null); }
    try {
      const { data: s } = await axios.get(`${API}/medications/dose-suggestion`, {
        params: {
          name: item.name, ingredient: item.ingredient,
          atc: item.atc, strength: item.strength, form: item.form,
        },
      });
      setForm((f) => ({
        ...f,
        name: item.name,
        dose: s.dose || f.dose,
        frequency: s.frequency || f.frequency,
        schedule: (s.schedule_times || []).join(", "),
      }));
      setAutoNote({
        ingredient: item.ingredient,
        text: [s.frequency, s.dose].filter(Boolean).join(" · "),
        food: s.food_rule_bg,
        note: s.note_bg,
        disclaimer: s.disclaimer_bg,
      });
    } catch {
      setAutoNote(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/medications/summary`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/medications`, {
        name: form.name.trim(), kind: form.kind,
        dose: form.dose || null, frequency: form.frequency || null,
        schedule_times: form.schedule
          ? form.schedule.split(",").map((s) => s.trim()).filter(Boolean) : null,
        pack_size: form.pack ? Number(form.pack) : null,
        pack_started_at: form.pack ? new Date().toISOString().slice(0, 10) : null,
      });
      setForm(EMPTY);
      setAutoNote(null);
      setPicked(null);
      setManual(false);
      setHerb(null);
      await load();
      await loadHerbActive();
      toast.success("Добавено в общия списък");
    } catch {
      toast.error("Добавянето не успя");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/medications/${id}`, {
        name: edit.name.trim(), kind: edit.kind,
        dose: edit.dose || null, frequency: edit.frequency || null,
        schedule_times: edit.schedule
          ? edit.schedule.split(",").map((s) => s.trim()).filter(Boolean) : [],
        pack_size: edit.pack ? Number(edit.pack) : null,
        pack_started_at: edit.pack ? new Date().toISOString().slice(0, 10) : null,
      });
      setEditId(null);
      await load();
      toast.success("Запазено");
    } catch {
      toast.error("Записването не успя");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/medications/${id}`);
      await load();
      await loadHerbActive();
    } catch {
      toast.error("Изтриването не успя");
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data: res } = await axios.post(`${API}/medications/import-freetext`);
      setImportDone(true);
      await load();
      toast.success(res.imported
        ? `Пренесени ${res.imported} записа от профила`
        : "Няма нови записи за пренасяне");
    } catch {
      toast.error("Пренасянето не успя");
    } finally {
      setImporting(false);
    }
  };

  const cardCls = isDark
    ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const inputCls = `px-2.5 py-1.5 rounded-lg text-[13px] border w-full ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  return (
    <div className={`p-4 rounded-2xl border overflow-hidden ${cardCls}`} id="meds" data-testid="profile-meds-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-sm font-bold flex items-center gap-2">
            <Pill className="w-4 h-4 text-violet-500" />
            Лекарства, добавки и билки
          </h2>
          <p className="text-[11px] opacity-60 mt-0.5">
            Един списък за цялото приложение — ползва се от чинията, гърдата,
            черния дроб, лабораториите и ум и тяло
          </p>
        </div>
        <button onClick={() => navigate("/dashboard/medications")}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-full border
                     hover:bg-violet-500/10 border-violet-500/40 text-violet-500
                     inline-flex items-center gap-1"
          data-testid="profile-meds-open-full">
          Взаимодействия и напомняния <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {/* пренасяне на старите записи от свободния текст */}
      {!!freetext && !importDone && (
        <div className="mt-3 rounded-xl p-3 bg-amber-500/10 border-l-[3px] border-amber-500"
          data-testid="profile-meds-import-banner">
          <div className="text-[12px] font-semibold text-amber-600 dark:text-amber-400">
            Открихме записани на ръка лекарства в профила
          </div>
          <div className="text-[11px] opacity-80 mt-0.5">„{freetext.slice(0, 120)}“</div>
          <button onClick={runImport} disabled={importing}
            className="mt-2 text-[11px] font-bold px-3 py-1.5 rounded-lg bg-amber-500
                       text-white hover:bg-amber-600 disabled:opacity-60 inline-flex items-center gap-1"
            data-testid="profile-meds-import-btn">
            {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {importing ? "Пренасям…" : "Пренеси ги в общия списък"}
          </button>
        </div>
      )}

      {/* дублирано действащо вещество */}
      {!!data?.duplicates?.length && (
        <div className="mt-3 space-y-2" data-testid="profile-meds-duplicates">
          {data.duplicates.map((w) => (
            <div key={w.code} className="rounded-xl p-3"
              style={{
                background: (w.severity === "high" ? "#EF4444" : "#F59E0B") + "14",
                borderLeft: `3px solid ${w.severity === "high" ? "#EF4444" : "#F59E0B"}`,
              }}
              data-testid={`profile-meds-duplicate-${w.code}`}>
              <div className="text-[11px] font-bold inline-flex items-center gap-1"
                style={{ color: w.severity === "high" ? "#EF4444" : "#F59E0B" }}>
                <Copy className="w-3 h-3" />
                {w.severity === "high" ? "Едно и също вещество" : "Една и съща група"}
              </div>
              <div className="text-[12px] mt-0.5">{w.message_bg}</div>
              <div className="text-[11px] mt-1 opacity-80">→ {w.advice_bg}</div>
            </div>
          ))}
        </div>
      )}

      {/* брояч по вид */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        {KINDS.map((k) => (
          <div key={k.key} className={`rounded-xl p-2.5 ${isDark ? "bg-white/5" : "bg-slate-50"}`}
            data-testid={`profile-meds-count-${k.key}`}>
            <div className="text-[9px] opacity-60 flex items-center gap-1">
              <k.Icon className="w-3 h-3" style={{ color: k.color }} /> {k.label}
            </div>
            <div className="text-lg font-bold">{data?.counts?.[k.key] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* добавяне */}
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-1.5 mt-3 min-w-0">
        <div className="sm:col-span-2 relative">
          <input className={inputCls} placeholder="Име (например Енап, Еутирокс)"
            value={form.name}
            onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setPicked(null); }}
            data-testid="profile-meds-input-name" />
          {!!suggest.length && (
            <div className={`absolute z-20 left-0 right-0 mt-1 rounded-xl border shadow-xl overflow-hidden
                            ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"}`}
              data-testid="profile-meds-suggest">
              {suggest.map((s) => (
                <button key={`${s.name}-${s.strength}`} onClick={() => pickFromRegistry(s)}
                  className={`w-full text-left px-3 py-2 text-[12px] ${isDark ? "hover:bg-white/5" : "hover:bg-slate-50"}`}
                  data-testid={`profile-meds-suggest-${s.name}`}>
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-[10px] opacity-60">
                    {s.ingredient}{s.strength ? ` · ${s.strength}` : ""}{s.form ? ` · ${s.form}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <select className={inputCls} value={form.kind}
          onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
          data-testid="profile-meds-input-kind">
          {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <input className={inputCls} placeholder="доза (50 mcg)" value={form.dose}
          onChange={(e) => setForm((f) => ({ ...f, dose: e.target.value }))}
          data-testid="profile-meds-input-dose" />
        <input className={inputCls} placeholder="часове (08:00)" value={form.schedule}
          onChange={(e) => setForm((f) => ({ ...f, schedule: e.target.value }))}
          data-testid="profile-meds-input-schedule" />
        <input className={inputCls} type="number" placeholder="бр. в опаковка" value={form.pack}
          onChange={(e) => setForm((f) => ({ ...f, pack: e.target.value }))}
          data-testid="profile-meds-input-pack" />
        <button onClick={add} disabled={saving || !form.name.trim() || (!picked && !manual)}
          className="text-[12px] font-bold px-3 py-2 rounded-xl bg-violet-500 text-white
                     hover:bg-violet-600 disabled:opacity-50 inline-flex items-center justify-center gap-1"
          data-testid="profile-meds-add-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Добави
        </button>
      </div>

      {!picked && !manual && form.name.trim().length >= 2 && (
        <div className="text-[11px] mt-2 opacity-70" data-testid="profile-meds-pick-hint">
          Изберете от списъка, за да няма грешка в името и в дозата.{" "}
          <button onClick={() => setManual(true)}
            className="underline font-semibold text-violet-500"
            data-testid="profile-meds-manual-btn">
            Не го намирам — добави ръчно
          </button>
        </div>
      )}

      {/* взаимодействия на избрания запис — показват се преди добавянето */}
      {!!herb?.warnings?.length && (
        <div className="mt-2 space-y-2" data-testid="profile-meds-herb-candidate">
          {herb.warnings.map((w) => (
            <div key={w.code} className="rounded-xl p-3 bg-red-500/12 border-l-[3px] border-red-500"
              data-testid={`profile-meds-herb-warning-${w.code}`}>
              <div className="text-[11px] font-bold text-red-500 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Внимание — взаимодействие с Ваше лекарство
              </div>
              <div className="text-[12px] mt-0.5 font-semibold">{w.med_bg}</div>
              <div className="text-[12px] mt-0.5">{w.message_bg}</div>
              <div className="text-[11px] mt-1 opacity-80">→ {w.advice_bg}</div>
            </div>
          ))}
        </div>
      )}
      {herb?.catalog?.drug_note_bg && !herb?.warnings?.length && (
        <div className="mt-2 rounded-xl p-2.5 bg-emerald-500/10 border-l-[3px] border-emerald-500"
          data-testid="profile-meds-herb-catalog">
          <div className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            От указателя · {herb.catalog.name_lat}
          </div>
          <div className="text-[11px] mt-0.5 opacity-85">{herb.catalog.drug_note_bg}</div>
          <div className="text-[10px] mt-1 opacity-70">
            Няма засечено взаимодействие с лекарствата от Вашия списък.
          </div>
        </div>
      )}

      {autoNote && (
        <div className="mt-2 rounded-xl p-2.5 bg-violet-500/10 border-l-[3px] border-violet-500"
          data-testid="profile-meds-auto-note">
          <div className="text-[11px] font-semibold text-violet-500">
            Попълнено автоматично от указателя{autoNote.ingredient ? ` · ${autoNote.ingredient}` : ""}
          </div>
          {autoNote.text && <div className="text-[12px] mt-0.5">{autoNote.text}</div>}
          {autoNote.note && <div className="text-[11px] mt-0.5 opacity-80">{autoNote.note}</div>}
          {autoNote.food && <div className="text-[11px] mt-0.5 opacity-80">{autoNote.food}</div>}
          <div className="text-[9px] opacity-55 mt-1">
            {autoNote.disclaimer} Променете стойностите, ако лекарят Ви е предписал друго.
          </div>
        </div>
      )}

      {/* списък */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : !data?.items?.length ? (
        <div className="text-[11px] opacity-60 mt-3" data-testid="profile-meds-empty">
          Списъкът е празен. Добавете първото лекарство или добавка —
          проверките в чинията и в останалите модули се включват веднага.
        </div>
      ) : (
        <div className="mt-3 space-y-1.5" data-testid="profile-meds-list">
          {KINDS.map((k) => {
            const items = data.items.filter((i) => i.kind === k.key);
            if (!items.length) return null;
            return (
              <div key={k.key}>
                <div className="text-[10px] font-bold opacity-50 mt-2 mb-1 uppercase">
                  {k.label}
                </div>
                {items.map((i) => (
                  <div key={i.id} className={`rounded-xl p-2.5 ${isDark ? "bg-white/5" : "bg-slate-50"}`}
                    data-testid={`profile-meds-item-${i.id}`}>
                    {editId === i.id ? (
                      <div className="grid grid-cols-1 sm:grid-cols-6 gap-1.5 min-w-0">
                        <input className={inputCls} value={edit.name}
                          onChange={(e) => setEdit((f) => ({ ...f, name: e.target.value }))} />
                        <select className={inputCls} value={edit.kind}
                          onChange={(e) => setEdit((f) => ({ ...f, kind: e.target.value }))}>
                          {KINDS.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                        </select>
                        <input className={inputCls} placeholder="доза" value={edit.dose}
                          onChange={(e) => setEdit((f) => ({ ...f, dose: e.target.value }))} />
                        <input className={inputCls} placeholder="часове" value={edit.schedule}
                          onChange={(e) => setEdit((f) => ({ ...f, schedule: e.target.value }))} />
                        <input className={inputCls} type="number" placeholder="бр. в опаковка"
                          value={edit.pack}
                          onChange={(e) => setEdit((f) => ({ ...f, pack: e.target.value }))} />
                        <div className="flex gap-1">
                          <button onClick={() => saveEdit(i.id)}
                            className="flex-1 text-[11px] font-bold rounded-lg bg-emerald-500 text-white py-1.5"
                            data-testid={`profile-meds-save-${i.id}`}>
                            <Check className="w-3.5 h-3.5 mx-auto" />
                          </button>
                          <button onClick={() => setEditId(null)}
                            className="flex-1 text-[11px] rounded-lg border py-1.5 opacity-70">
                            <X className="w-3.5 h-3.5 mx-auto" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold truncate">{i.name}</div>
                          <div className="text-[10px] opacity-60 flex items-center gap-2 flex-wrap mt-0.5">
                            {i.dose && <span>{i.dose}</span>}
                            {i.frequency && <span>{i.frequency}</span>}
                            {!!i.schedule_times?.length && (
                              <span className="inline-flex items-center gap-0.5">
                                <Clock className="w-2.5 h-2.5" /> {i.schedule_times.join(", ")}
                              </span>
                            )}
                            {i.pack && (
                              <span className={`px-1.5 py-0.5 rounded-full font-bold ${
                                i.pack.status === "ok" ? "bg-emerald-500/15 text-emerald-500"
                                  : i.pack.status === "soon" ? "bg-amber-500/15 text-amber-500"
                                    : "bg-red-500/15 text-red-500"}`}
                                data-testid={`profile-meds-pack-${i.id}`}>
                                {i.pack.days_left
                                  ? `опаковка: още ${i.pack.days_left} дни`
                                  : "опаковката свърши"}
                              </span>
                            )}
                            {i.food_rule_label_bg && (
                              <span className="px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-500 font-bold"
                                data-testid={`profile-meds-foodrule-${i.id}`}>
                                {i.food_rule_label_bg}
                              </span>
                            )}
                          </div>
                          {i.food_rule_bg && (
                            <div className="text-[10px] opacity-55 mt-1">{i.food_rule_bg}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => {
                            setEditId(i.id);
                            setEdit({
                              name: i.name || "", kind: i.kind || "drug",
                              dose: i.dose || "", frequency: i.frequency || "",
                              schedule: (i.schedule_times || []).join(", "),
                              pack: i.pack?.pack_size ? String(i.pack.pack_size) : "",
                            });
                          }} className="opacity-40 hover:opacity-100"
                            data-testid={`profile-meds-edit-${i.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => remove(i.id)} className="opacity-40 hover:opacity-100"
                            data-testid={`profile-meds-delete-${i.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* постоянен списък с активните взаимодействия билка/добавка × лекарство */}
      {!!herbActive.length && (
        <div className="mt-3 space-y-2" data-testid="profile-meds-herb-active">
          <div className="text-[10px] font-bold opacity-50 uppercase">
            Активни взаимодействия във Вашия списък
          </div>
          {herbActive.map((w) => (
            <div key={w.code} className="rounded-xl p-3 bg-amber-500/12 border-l-[3px] border-amber-500"
              data-testid={`profile-meds-herb-active-${w.code}`}>
              <div className="text-[11px] font-bold text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
                <Leaf className="w-3 h-3" /> {w.med_bg}
              </div>
              <div className="text-[12px] mt-0.5">{w.message_bg}</div>
              <div className="text-[11px] mt-1 opacity-80">→ {w.advice_bg}</div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[9px] opacity-50 mt-2">{data?.disclaimer_bg}</div>
    </div>
  );
}
